import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';

const MyTeachersScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [subjectTeachers, setSubjectTeachers] = useState([]);
  const [classTeacher, setClassTeacher] = useState(null);

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setInitialLoading(true);
      await Promise.all([loadSubjectTeachers(), loadClassTeacher()]);
    } catch (error) {
      console.error('Error initializing MyTeachersScreen:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadSubjectTeachers = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        '/api/master/school-class-subject-teacher/get-my-class-subject-teacher',
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setSubjectTeachers(response.data || []);
    } catch (error) {
      console.error('Error loading subject teachers:', error);
      handleCatch(error);
      setSubjectTeachers([]);
    }
  };

  const loadClassTeacher = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('/api/master/school-class-teacher/get-my-class-teacher', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setClassTeacher(response.data);
    } catch (error) {
      console.error('Error loading class teacher:', error);
      handleCatch(error);
      setClassTeacher(null);
    }
  };

  const onRefresh = async () => {
    try {
      setLoading(true);
      await Promise.all([loadSubjectTeachers(), loadClassTeacher()]);
    } catch (error) {
      console.error('Error refreshing teachers data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUri = (item) => {
    if (!item.photo) return null;
    if (item.photo.startsWith('http')) return item.photo;
    if (item.photo.startsWith('/')) return `https://billtronapi.hellopatna.com${item.photoPath}`;
    return `data:image/png;base64,${item.photo}`;
  };

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : '?');

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title="My Teachers"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading || initialLoading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 10 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>
              Loading teachers...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Class Teacher */}
            <Box sx={{ mx: 0.5, mt: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                Class Teacher
              </Typography>
              {classTeacher ? (
                <Box
                  sx={{
                    bgcolor: '#E3F2FD',
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: '4px solid #1976D2',
                  }}
                >
                  <Box sx={{ position: 'relative', mr: 2 }}>
                    <Avatar
                      src={getPhotoUri(classTeacher)}
                      sx={{ width: 52, height: 52, bgcolor: '#1976D2', fontWeight: 'bold', fontSize: 22, border: '2px solid #1976D2' }}
                    >
                      {getInitial(classTeacher.teacher)}
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -3,
                        right: -3,
                        bgcolor: '#FF6B35',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #E3F2FD',
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontSize: 8, fontWeight: 'bold' }}>CT</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', color: '#1976D2', fontSize: 15 }}>
                      {classTeacher.teacher}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#555' }}>Class Teacher</Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 2, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 40, mb: 0.5 }}></Typography>
                  <Typography sx={{ fontSize: 13, color: '#888' }}>No class teacher assigned</Typography>
                </Box>
              )}
            </Box>

            {/* Subject Teachers */}
            <Box sx={{ mx: 0.5, mt: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                Subject Teachers ({subjectTeachers.length})
              </Typography>
              {subjectTeachers.length === 0 ? (
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 40, mb: 0.5 }}></Typography>
                  <Typography sx={{ fontSize: 13, color: '#888' }}>No subject teachers found</Typography>
                </Box>
              ) : (
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                  {subjectTeachers.map((teacher, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider sx={{ ml: 8 }} />}
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.4 }}>
                        <Avatar
                          src={getPhotoUri(teacher)}
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: colors.BLUE,
                            fontWeight: 'bold',
                            fontSize: 18,
                            border: '2px solid #0066CC',
                            mr: 1.5,
                          }}
                        >
                          {getInitial(teacher.teacher)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: '#222' }}>
                            {teacher.teacher}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: colors.BLUE, fontWeight: '500' }}>
                            {teacher.subject}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 22 }}></Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MyTeachersScreen;
